import {
  Users,
  TrendingUp,
  Inbox,
  AlertTriangle,
  PoundSterling,
  Clock,
  UserMinus,
  UserCheck,
  CalendarPlus,
  Flame,
  BarChart2,
  AlertCircle,
  Info,
  Construction,
  Loader2,
} from 'lucide-react';
import { KPI } from '@/components/ui/kpi';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DataModeToggle } from '@/components/DataModeToggle';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { HelpButton } from '@/components/help/HelpButton';
import { useDataMode } from '@/contexts/DataModeContext';
import { useDashboardData } from '@/hooks/useDashboardData';

// ─── Types ────────────────────────────────────────────────────────────────────

type AppPage =
  | 'overview' | 'calendar' | 'heatmap' | 'resources' | 'projects'
  | 'settings' | 'open-demand-requests' | 'open-demand-unallocated' | 'dependent-fields' | 'report-utilisation'
  | 'report-utilisation-forecast' | 'report-available-capacity'
  | 'report-resource-allocation' | 'report-project-staffing' | 'report-bench'
  | 'report-audit-log' | 'roles-permissions' | 'roles-permissions-edit' | 'user-setup';

interface DashboardOverviewProps {
  onNavigate?: (page: AppPage) => void;
}

// ─── Demo Data (used in demo mode only) ───────────────────────────────────────

const demoTrendData = [
  { week: 'W19', pct: 65 },
  { week: 'W20', pct: 72 },
  { week: 'W21', pct: 78 },
  { week: 'W22', pct: 81 },
  { week: 'W23', pct: 76 },
  { week: 'W24', pct: 83 },
  { week: 'W25', pct: 79 },
  { week: 'W26', pct: 78 },
];

const demoAtRiskProjects = [
  { name: 'ERP Migration',    status: 'red',   pct: 112 },
  { name: 'Mobile App v3',    status: 'amber', pct: 87  },
  { name: 'Data Platform',    status: 'amber', pct: 82  },
  { name: 'Website Redesign', status: 'green', pct: 61  },
  { name: 'CRM Integration',  status: 'green', pct: 45  },
];

const demoOverloadedResources = [
  { name: 'Sarah Chen',    role: 'Senior Developer',  pct: 118 },
  { name: 'James Wilson',  role: 'Tech Lead',          pct: 112 },
  { name: 'Emily Park',    role: 'UX Designer',        pct: 105 },
  { name: 'Michael Brown', role: 'DevOps Engineer',    pct: 101 },
  { name: 'Lisa Kumar',    role: 'BA',                 pct: 98  },
];

const kpiTooltips = {
  totalResources:
    'Total active resources in your team directory.',
  teamUtilisation:
    'Average utilisation across all resources for the current week, calculated as confirmed booking hours ÷ available working hours. The target threshold and tier bands (on target / watch / under-utilised) are configured company-wide in Settings → General → Utilisation Tiers.',
  openDemand:
    'Unallocated resource requests (open demand). Critical = overdue or flagged urgent. Soon = required within 2 weeks. Upcoming = required within 4 weeks.',
  burnoutRisk:
    'Resources identified as at risk of burnout based on sustained high utilisation (>100%) over multiple consecutive weeks. Powered by the Burnout Detection algorithm. See Settings → General to adjust sensitivity.',
  budgetHealth:
    'Percentage of active projects within their allocated resource budget. "On track" = <80% of resource budget consumed, "At risk" = 80–99% consumed, "Over" = resource budget exceeded.',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function barColour(pct: number): string {
  if (pct >= 80) return 'bg-green-500';
  if (pct >= 60) return 'bg-amber-500';
  return 'bg-red-500';
}

function ragDot(status: string) {
  if (status === 'red')   return <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1.5 shrink-0" />;
  if (status === 'amber') return <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1.5 shrink-0" />;
  return <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1.5 shrink-0" />;
}

function ragBadgeAccent(status: string): 'destructive' | 'warning' | 'success' {
  if (status === 'red')   return 'destructive';
  if (status === 'amber') return 'warning';
  return 'success';
}

function utilBadgeAccent(pct: number): 'destructive' | 'warning' | 'success' {
  if (pct > 100) return 'destructive';
  if (pct >= 90) return 'warning';
  return 'success';
}

function getUtilAccent(pct: number): 'destructive' | 'warning' | 'success' {
  if (pct >= 80) return 'success';
  if (pct >= 60) return 'warning';
  return 'destructive';
}

// ─── Sub-component: KPI info icon with tooltip ────────────────────────────────

interface KpiInfoTooltipProps {
  content: string;
}

function KpiInfoTooltip({ content }: KpiInfoTooltipProps) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="More information"
            className="inline-flex items-center justify-center rounded-full p-1 bg-card text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Info className="w-5 h-5" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="center"
          className="max-w-xs text-xs leading-relaxed"
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DashboardOverview({ onNavigate }: DashboardOverviewProps) {
  const { isLive } = useDataMode();
  const { data: liveData, isLoading, error } = useDashboardData();

  const isLiveMode = isLive('overview');
  // Live mode without a summary for the selected company yet (first load, company
  // switch or a failed request): show placeholders rather than demo figures or the
  // previously selected company's figures.
  const awaitingLiveData = isLiveMode && !liveData;
  const placeholder = '—';

  // Derive values depending on mode
  const totalResources = isLiveMode && liveData ? liveData.totalResources : 24;
  const teamUtilisation = isLiveMode && liveData ? liveData.teamUtilisation : 78;
  const openDemandTotal = isLiveMode && liveData ? liveData.openDemand.total : 6;
  const openDemandCritical = isLiveMode && liveData ? liveData.openDemand.critical : 2;
  const openDemandSoon = isLiveMode && liveData ? liveData.openDemand.soon : 3;
  const openDemandUpcoming = isLiveMode && liveData ? liveData.openDemand.upcoming : 1;
  const burnoutTotal = isLiveMode && liveData ? liveData.burnoutRisk.total : 3;
  const burnoutCritical = isLiveMode && liveData ? liveData.burnoutRisk.critical : 1;
  const burnoutHigh = isLiveMode && liveData ? liveData.burnoutRisk.high : 2;
  const budgetHealthPct = isLiveMode && liveData ? liveData.budgetHealth.percentOnTrack : 82;
  const budgetOnTrack = isLiveMode && liveData ? liveData.budgetHealth.onTrack : 8;
  const budgetAtRisk = isLiveMode && liveData ? liveData.budgetHealth.atRisk : 2;
  const budgetOver = isLiveMode && liveData ? liveData.budgetHealth.over : 1;

  const forecastUtilisation = isLiveMode && liveData ? liveData.forecastUtilisation : 72;
  const availableCapacity = isLiveMode && liveData ? liveData.availableCapacityHours : 480;
  const benchResources = isLiveMode && liveData ? liveData.benchResources : 3;
  const comingFree = isLiveMode && liveData ? liveData.comingFreeResources : 2;

  const trendData = isLiveMode
    ? (liveData?.utilisationTrend.map(t => ({ week: t.week, pct: t.pct })) ?? [])
    : demoTrendData;

  const atRiskProjects = isLiveMode
    ? (liveData?.projectsRequiringAttention.map(p => ({ name: p.name, status: p.status, pct: p.pct })) ?? [])
    : demoAtRiskProjects;

  const overloadedResources = isLiveMode
    ? (liveData?.overloadedResources.map(r => ({ name: r.name, role: r.role, pct: r.pct })) ?? [])
    : demoOverloadedResources;

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
      <div className="w-full p-6 space-y-6">

        {/* ── Page Header ─────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
              <BarChart2 className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold leading-tight">Dashboard Overview</h1>
              <p className="text-muted-foreground text-sm mt-0.5">
                Your morning snapshot — team health, demand, and capacity at a glance
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <DataModeToggle screen="overview" />
            <FeatureFeedbackCollector feature="Dashboard" />
            <HelpButton />
          </div>
        </div>

        {/* ── Loading / Error State ─────────────────────────────────── */}
        {isLiveMode && isLoading && (
          <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading live dashboard data...</span>
          </div>
        )}

        {isLiveMode && error && !isLoading && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-sm text-destructive font-medium">Failed to load live data</p>
            <p className="text-xs text-muted-foreground mt-1">{error}</p>
          </div>
        )}

        {/* ── Row 1 — Primary KPI Cards ───────────────────────────────── */}
        <div data-tour="overview-kpis" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">

          {/* 1 — Total Resources */}
          <div className="relative [&_span:has(svg)]:hidden">
            <KPI
              variant="default"
              label="Total Resources"
              value={awaitingLiveData ? placeholder : totalResources}
              icon={<Users className="w-7 h-7" />}
              accent="info"
            />
            <div className="absolute top-3 right-3">
              <KpiInfoTooltip content={kpiTooltips.totalResources} />
            </div>
          </div>

          {/* 2 — Team Utilisation */}
          <div className="relative [&_span:has(svg)]:hidden">
            <KPI
              variant="default"
              label="Team Utilisation"
              value={awaitingLiveData ? placeholder : `${teamUtilisation}%`}
              description="Current week average"
              icon={<TrendingUp className="w-7 h-7" />}
              accent={getUtilAccent(teamUtilisation)}
            />
            <div className="absolute top-3 right-3">
              <KpiInfoTooltip content={kpiTooltips.teamUtilisation} />
            </div>
          </div>

          {/* 3 — Open Demand */}
          <div className="relative [&_span:has(svg)]:hidden">
            <KPI
              variant="default"
              label="Open Demand"
              value={awaitingLiveData ? placeholder : openDemandTotal}
              description={
                awaitingLiveData
                  ? placeholder
                  : `${openDemandCritical} critical · ${openDemandSoon} soon · ${openDemandUpcoming} upcoming`
              }
              icon={<Inbox className="w-7 h-7" />}
              accent={openDemandCritical > 0 ? 'destructive' : 'warning'}
            />
            <div className="absolute top-3 right-3">
              <KpiInfoTooltip content={kpiTooltips.openDemand} />
            </div>
          </div>

          {/* 4 — Burnout Risk */}
          <div className="relative [&_span:has(svg)]:hidden">
            <KPI
              variant="default"
              label="Burnout Risk"
              value={awaitingLiveData ? placeholder : burnoutTotal}
              description={
                awaitingLiveData ? placeholder : `${burnoutCritical} critical · ${burnoutHigh} high`
              }
              icon={<AlertTriangle className="w-7 h-7" />}
              accent={burnoutTotal > 0 ? 'warning' : 'success'}
            />
            <div className="absolute top-3 right-3">
              <KpiInfoTooltip content={kpiTooltips.burnoutRisk} />
            </div>
          </div>

          {/* 5 — Budget Health */}
          <div className="relative [&_span:has(svg)]:hidden">
            <KPI
              variant="default"
              label="Budget Health"
              value={awaitingLiveData ? placeholder : `${budgetHealthPct}%`}
              description={
                awaitingLiveData
                  ? placeholder
                  : `${budgetOnTrack} on track · ${budgetAtRisk} at risk · ${budgetOver} over`
              }
              icon={<PoundSterling className="w-7 h-7" />}
              accent={budgetHealthPct >= 70 ? 'success' : budgetHealthPct >= 50 ? 'warning' : 'destructive'}
            />
            <div className="absolute top-3 right-3">
              <KpiInfoTooltip content={kpiTooltips.budgetHealth} />
            </div>
          </div>

        </div>

        {/* ── Row 2 — Secondary KPI Cards ─────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="rounded-xl border border-border shadow-sm">
            <CardContent className="pt-5 pb-5">
              <KPI
                variant="mini"
                label="Forecast Utilisation"
                value={awaitingLiveData ? placeholder : `${forecastUtilisation}%`}
                description="Next 4 weeks projected"
                icon={<TrendingUp className="w-4 h-4" />}
                accent="lagoon"
              />
            </CardContent>
          </Card>

          <Card className="rounded-xl border border-border shadow-sm">
            <CardContent className="pt-5 pb-5">
              <KPI
                variant="mini"
                label="Available Capacity"
                value={awaitingLiveData ? placeholder : `${availableCapacity}h`}
                description="Next 4 weeks unbooked"
                icon={<Clock className="w-4 h-4" />}
                accent="cerulean"
              />
            </CardContent>
          </Card>

          <Card className="rounded-xl border border-border shadow-sm">
            <CardContent className="pt-5 pb-5">
              <KPI
                variant="mini"
                label="Bench Resources"
                value={awaitingLiveData ? placeholder : benchResources}
                description="Below 20% utilisation"
                icon={<UserMinus className="w-4 h-4" />}
                accent="orange"
              />
            </CardContent>
          </Card>

          <Card className="rounded-xl border border-border shadow-sm">
            <CardContent className="pt-5 pb-5">
              <KPI
                variant="mini"
                label="Coming Free"
                value={awaitingLiveData ? placeholder : comingFree}
                description="Capacity freeing up within 8 weeks"
                icon={<UserCheck className="w-4 h-4" />}
                accent="sap"
              />
            </CardContent>
          </Card>
        </div>

        {/* ── Row 3 — Utilisation Trend Chart ─────────────────────────── */}
        <Card data-tour="overview-trend" className="rounded-xl border border-border shadow-sm">
          <CardHeader>
            <CardTitle>Utilisation Trend</CardTitle>
            <CardDescription>
              Team average over the last 8 weeks — thresholds reflect target utilisation
              configured in Settings → General → Utilisation Tiers
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Bar chart */}
            <div className="h-48 flex items-end gap-2">
              {awaitingLiveData && (
                <p className="text-sm text-muted-foreground self-center">
                  No utilisation trend for the selected company yet
                </p>
              )}
              {trendData.map((d) => (
                <div key={d.week} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
                  <span className="text-xs font-medium text-foreground">{d.pct}%</span>
                  <div
                    className={`w-full rounded-t transition-all ${barColour(d.pct)}`}
                    style={{ height: `${(d.pct / 100) * 160}px` }}
                  />
                  <span className="text-xs text-muted-foreground">{d.week}</span>
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-green-500" />
                <span className="text-xs text-muted-foreground">≥ 80% — On target</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-amber-500" />
                <span className="text-xs text-muted-foreground">60–79% — Watch</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-red-500" />
                <span className="text-xs text-muted-foreground">&lt; 60% — Under-utilised</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Row 4 — Alert Banners ────────────────────────────────────── */}
        <div className="flex flex-col gap-3">

          {/* Burnout alert */}
          {!awaitingLiveData && burnoutTotal > 0 && (
            <div
              role="alert"
              className="accent-warning flex items-center justify-between gap-4 bg-[var(--accent-bg-outline)] border border-[var(--accent-border)] rounded-lg p-4"
            >
              <div className="flex items-center gap-3">
                <AlertCircle
                  className="w-5 h-5 shrink-0"
                  aria-hidden="true"
                  style={{ color: 'var(--accent-text-on-outline)' }}
                />
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--accent-text-on-outline)' }}>
                    Burnout Risk Alert
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {burnoutTotal} resources are at burnout risk — review workload and rebalance assignments
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => onNavigate?.('heatmap')}
                aria-label="View burnout suggestions on heatmap"
              >
                View Suggestions
              </Button>
            </div>
          )}

          {/* Coming Free info */}
          {!awaitingLiveData && comingFree > 0 && (
            <div
              role="status"
              className="accent-info flex items-center justify-between gap-4 bg-[var(--accent-bg-outline)] border border-[var(--accent-border)] rounded-lg p-4"
            >
              <div className="flex items-center gap-3">
                <Info
                  className="w-5 h-5 shrink-0"
                  aria-hidden="true"
                  style={{ color: 'var(--accent-text-on-outline)' }}
                />
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--accent-text-on-outline)' }}>
                    Upcoming Availability
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {comingFree} resources becoming available in the next 8 weeks — plan ahead to avoid bench time
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => onNavigate?.('report-available-capacity')}
                aria-label="View resources with upcoming availability"
              >
                View Resources
              </Button>
            </div>
          )}

        </div>

        {/* ── Row 5 — Quick Actions ────────────────────────────────────── */}
        <Card className="rounded-xl border border-border shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription className="mt-1">
                  Shortcuts to your most-used workflows
                </CardDescription>
              </div>
              <Badge variant="outline" className="flex items-center gap-1.5 shrink-0 text-muted-foreground">
                <Construction className="w-3 h-3" />
                Demo only — MFEs coming
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" disabled aria-label="New Booking — available via MFE">
                <CalendarPlus className="w-4 h-4 mr-2" aria-hidden="true" />
                New Booking
              </Button>
              <Button variant="outline" disabled aria-label="Open Demand — available via MFE">
                <Inbox className="w-4 h-4 mr-2" aria-hidden="true" />
                Open Demand
              </Button>
              <Button variant="outline" disabled aria-label="View Heatmap — available via MFE">
                <Flame className="w-4 h-4 mr-2" aria-hidden="true" />
                View Heatmap
              </Button>
              <Button variant="outline" disabled aria-label="Utilisation Report — available via MFE">
                <BarChart2 className="w-4 h-4 mr-2" aria-hidden="true" />
                Utilisation Report
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              These actions are planned for delivery as MFEs through Evo Navigator.
              They will become independently deployable widgets surfaced across host applications.
            </p>
          </CardContent>
        </Card>

        {/* ── Row 6 — Summary Tables ───────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Left — Projects Requiring Attention */}
          <Card className="rounded-xl border border-border shadow-sm">
            <CardHeader>
              <CardTitle>Projects Requiring Attention</CardTitle>
              <CardDescription>Budget consumption across active projects</CardDescription>
            </CardHeader>
            <CardContent>
              {atRiskProjects.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {awaitingLiveData ? placeholder : 'No projects requiring attention'}
                </p>
              ) : (
                <div className="space-y-4">
                  {atRiskProjects.map((project) => (
                    <div key={project.name} className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-0 min-w-0">
                          {ragDot(project.status)}
                          <span className="text-sm font-medium truncate">{project.name}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-muted-foreground">{project.pct}%</span>
                          <Badge
                            variant={ragBadgeAccent(project.status) as 'destructive' | 'warning' | 'success'}
                            className="text-xs capitalize"
                          >
                            {project.status === 'red' ? 'Over' : project.status === 'amber' ? 'At Risk' : 'On Track'}
                          </Badge>
                        </div>
                      </div>
                      <Progress
                        value={Math.min(project.pct, 100)}
                        accent={ragBadgeAccent(project.status) as 'destructive' | 'warning' | 'success'}
                        variant="mini"
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right — Most Overloaded Resources */}
          <Card className="rounded-xl border border-border shadow-sm">
            <CardHeader>
              <CardTitle>Most Overloaded Resources</CardTitle>
              <CardDescription>
                Resources currently exceeding their target utilisation — thresholds reflect
                individual targets set in Settings → General → Utilisation Tiers
              </CardDescription>
            </CardHeader>
            <CardContent>
              {overloadedResources.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {awaitingLiveData ? placeholder : 'No overloaded resources'}
                </p>
              ) : (
                <div className="space-y-4">
                  {overloadedResources.map((resource) => (
                    <div key={resource.name} className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{resource.name}</p>
                          <p className="text-xs text-muted-foreground">{resource.role}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge
                            variant={utilBadgeAccent(resource.pct) as 'destructive' | 'warning' | 'success'}
                            className="text-xs"
                          >
                            {resource.pct}%
                          </Badge>
                        </div>
                      </div>
                      <Progress
                        value={Math.min(resource.pct, 100)}
                        accent={utilBadgeAccent(resource.pct) as 'destructive' | 'warning' | 'success'}
                        variant="mini"
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

        </div>

      </div>
    </div>
  );
}
